import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {setURLParams} from 'BenchlingUtil';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {CreateStepParams} from 'pipelines/network/PipelineDAO';
import EntityStore from 'stores/EntityStore';
import * as net from 'util/Net';
import {JSONValue} from 'util/Types';

export function analysisTemplatePageUrl(analysisTemplateId: string): string {
  return setURLParams(
    `/${EntityStore.currentUser().url_identifier}/analysis-templates/${analysisTemplateId}`,
    {}
  );
}

// Keep in sync with benchling/pipelines/step_type_library/step_config_shapes.py
export const UNSET_CONFIG_VARIABLE = 'UNSET';

export interface CreateAnalysisTemplateDataFrameVariableParams {
  description: string;
  name: string;
  replaceStepId: string;
  replaceStepDataId: string;
  replaceStepDataIndex: number;
  replaceDataFrameColumnsSpec: Array<DataFrameColumnDef>;
}

export interface CreateAnalysisTemplateParams {
  dataFrameVariables: Array<CreateAnalysisTemplateDataFrameVariableParams>;
  name: string;
  createdFromPipelineId: string | null;
  templateCollectionId: string;
  additionalSteps: Array<Omit<CreateStepParams, 'runAfterCreate'>>;
}

interface CreatedAnalysisTemplateResponseShape {
  createdId: string;
}

export interface CreatedAnalysisTemplateResponse {
  createdTemplates: Array<CreatedAnalysisTemplateResponseShape>;
}

export function createAnalysisTemplate(
  template: CreateAnalysisTemplateParams
): Promise<CreatedAnalysisTemplateResponse> {
  const creates = [template];
  return net.postJSONPromise({
    url: getApiBaseForObjectType(DomainGraphType.ANALYSIS_TEMPLATE),
    data: {creates},
  });
}

export interface CopyAnalysisTemplateParams {
  name: string;
  copiedFromAnalysisTemplateId: string;
  templateCollectionId: string;
}

export interface CopyAnalysisTemplateResponse {
  newAnalysisTemplateId: string;
}

export function copyAnalysisTemplate(
  copyParams: CopyAnalysisTemplateParams
): Promise<CopyAnalysisTemplateResponse> {
  const {name, templateCollectionId, copiedFromAnalysisTemplateId} = copyParams;
  return net.postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.ANALYSIS_TEMPLATE)}/${copiedFromAnalysisTemplateId}:clone`,
    data: {name, templateCollectionId},
  });
}

export interface TemplateVariableBinding {
  variableId: string;
  variableValue: JSONValue;
}

interface DataFrameVariableBinding {
  variableId: string;
  newDataFrameId: string;
  newInputStepDataId: string;
  columnMapping: {[templateColumn: string]: string};
}

export interface ApplyAnalysisTemplateParams {
  dataFrameVariableBindings: Array<DataFrameVariableBinding>;
  templateId: string;
  templateVariableBindings: Array<TemplateVariableBinding>;
}

export function applyAnalysisTemplate(pipelineId: string, data: ApplyAnalysisTemplateParams): Promise<{}> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:apply-template`, data});
}

export interface AddTemplateInputTableParams {
  dataFrameId: string;
  name: string;
  isVariable: boolean;
  description?: string;
}

export interface AddTemplateInputTableRequestParams {
  templateInputToAdd: AddTemplateInputTableParams;
}

export interface AddTemplateInputTableResponse {
  templateId: string;
  newStepId: string;
}

export function addAnalysisTemplateSourceStep(
  analysisTemplateId: string,
  data: AddTemplateInputTableRequestParams
): Promise<AddTemplateInputTableResponse> {
  return net.postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.ANALYSIS_TEMPLATE)}/${analysisTemplateId}:add-source-step`,
    data,
  });
}

export interface UpdateAnalysisTemplateParams {
  id: string;
  name?: string;
  templateCollectionId?: string;
  shouldPublish?: boolean;
}

export interface UpdateAnalysisTemplateResponse {
  updatedIds: Array<string>;
}

export function updateAnalysisTemplate(
  update: UpdateAnalysisTemplateParams
): Promise<UpdateAnalysisTemplateResponse> {
  const updates = [update];
  return net.patchJSONPromise({
    url: getApiBaseForObjectType(DomainGraphType.ANALYSIS_TEMPLATE),
    data: {updates},
  });
}
